import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Litepicker from "litepicker";
import moment from "moment";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { IoSearchSharp } from "react-icons/io5";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import Select, { components } from "react-select";

const emptyString = {
    date: " --- ",
    time: " --- ",
    gender: "ALL",
    rooms: "ALL",
    sensors: "( 0 )",
};

const GENDERS = ["MALE", "FEMALE"];
const SENSORS = ["AMMONIA", "SOAP", "HAND TOWEL", "PAPER ROLL"];
// 282.83
/**
 * QueryBlock component
 * @param {Object} props - props object
 * @param {string} props.selectedBuilding - selected building
 * @param {boolean} props.enableQueryDateRange - enable query date range
 * @param {boolean} props.enableQueryHour - enable query hour
 * @param {boolean} props.enableQueryGender - enable query gender
 * @param {boolean} props.enableQueryRooms - enable query rooms
 * @param {boolean} props.enableQuerySensors - enable query sensors
 * @param {string} props.queryStartDate - query start date
 * @param {React.Dispatch<React.SetStateAction<string>>} props.setQueryStartDate - set query start date
 * @param {string} props.queryEndDate - query end date
 * @param {React.Dispatch<React.SetStateAction<string>>} props.setQueryEndDate - set query end date
 * @param {string} props.queryHour - query hour
 * @param {React.Dispatch<React.SetStateAction<string>>} props.setQueryHour - set query hour
 * @param {string} props.queryGender - query gender
 * @param {React.Dispatch<React.SetStateAction<string>>} props.setQueryGender - set query gender
 * @param {string[]} props.queryRooms - query rooms
 * @param {React.Dispatch<React.SetStateAction<string[]>} props.setQueryRooms - set query rooms
 * @param {string[]} props.querySensors - query sensors
 * @param {React.Dispatch<React.SetStateAction<string[]>} props.setQuerySensors - set query sensors
 * @param {function} props.handleSubmit - handle submit function
 * @returns {JSX.Element} QueryBlock component
 */
const QueryBlock = ({
    selectedBuilding,
    enableQueryDateRange = true,
    enableQueryHour = true,
    enableQueryGender = true,
    enableQueryRooms = true,
    enableQuerySensors = true,
    queryStartDate,
    setQueryStartDate,
    queryEndDate,
    setQueryEndDate,
    queryHour,
    setQueryHour,
    queryGender,
    setQueryGender,
    queryRooms,
    setQueryRooms,
    querySensors,
    setQuerySensors,
    handleSubmit,
    isLoading,
}) => {
    const room = useSelector((state) => state.room.data);

    const { enqueueSnackbar } = useSnackbar();

    const pickerRef = useRef(null);
    const dateRangeRef = useRef(null);
    const selectRef = useRef(null);

    const [displayDate, setDisplayDate] = useState(emptyString.date);
    const [displayTime, setDisplayTime] = useState(emptyString.time);
    const [displayGender, setDisplayGender] = useState(emptyString.gender);
    const [displayRooms, setDisplayRooms] = useState(emptyString.rooms);
    const [displaySensors, setDisplaySensors] = useState(emptyString.sensors);
    const [displayForm, setDisplayForm] = useState(false);
    const [roomOptions, setRoomOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [inputValue, setInputValue] = useState("");

    const Option = (props) => {
        return (
            <div className="query-room-option">
                <components.Option {...props} />
            </div>
        );
    };

    const GroupHeading = (props) => {
        return (
            <div className="query-room-group-heading">
                <components.GroupHeading {...props} />
                <div
                    className="query-room-group-heading-select-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        const info = props.data.label;
                        if (info) {
                            const newSelected = selectedOptions.filter((ele) => !ele.value.startsWith(info));
                            newSelected.push(...roomOptions.find((ele) => ele.label === info).options);
                            newSelected.sort((a, b) => sortRooms(a, b));
                            setSelectedOptions(newSelected);
                            setInputValue("");
                        }
                    }}
                >
                    Select This Floor
                </div>
            </div>
        );
    };

    function expandForm() {
        setDisplayForm(!displayForm);
    }

    function sortRooms(a, b) {
        const aRawFloor = a.value.split("-")[1];
        const bRawFloor = b.value.split("-")[1];

        const aFloor = aRawFloor?.replace("B", "-").match(/-?\d+/)[0];
        const bFloor = bRawFloor?.replace("B", "-").match(/-?\d+/)[0];

        return aRawFloor?.startsWith("B") && bRawFloor.startsWith("B")
            ? Number(bFloor) - Number(aFloor)
            : Number(aFloor) - Number(bFloor);
    }

    useEffect(() => {
        const options = {};
        Object.keys(room).forEach((roomName) => {
            if (room[roomName].buildingId === selectedBuilding) {
                const floor = `${room[roomName].buildingId}-${room[roomName].floor}`;
                if (floor in options) {
                    options[floor].options.push({ value: roomName, label: roomName });
                } else {
                    options[floor] = {
                        label: floor,
                        floor: room[roomName].floor,
                        options: [{ value: roomName, label: roomName }],
                    };
                }
            }
        });
        const roomOpt = Object.values(options);
        roomOpt.sort((a, b) => {
            const aFloor = a.floor.replace("B", "-");
            const bFloor = b.floor.replace("B", "-");

            return a.floor.startsWith("B") && b.floor.startsWith("B")
                ? Number(bFloor) - Number(aFloor)
                : Number(aFloor) - Number(bFloor);
        });
        setRoomOptions(roomOpt);
        setSelectedOptions([]);
    }, [room, selectedBuilding]);

    useEffect(() => {
        if (setQueryStartDate && setQueryEndDate && enableQueryDateRange && displayForm) {
            pickerRef.current = new Litepicker({
                element: dateRangeRef.current,
                allowRepick: true,
                autoApply: true,
                singleMode: false,
                lang: "en-SG",
                inlineMode: true,
                startDate: queryStartDate ? moment(queryStartDate, "YYYYMMDD").toDate() : null,
                endDate: queryEndDate ? moment(queryEndDate, "YYYYMMDD").toDate() : null,
                resetButton: true,
            });
            pickerRef.current.on("selected", (date1, date2) => {
                setQueryStartDate(moment(date1.dateInstance).format("YYYYMMDD"));
                setQueryEndDate(moment(date2.dateInstance).format("YYYYMMDD"));
            });
        } else {
            if (pickerRef.current) {
                pickerRef.current.destroy();
            }
        }
        return () => {
            if (pickerRef.current) {
                pickerRef.current.destroy();
            }
        };
    }, [setQueryStartDate, setQueryEndDate, enableQueryDateRange, displayForm]);

    useEffect(() => {
        if (queryStartDate === "" || queryEndDate === "" || !enableQueryDateRange || !setDisplayDate) {
            setDisplayDate(emptyString.date);
        } else {
            setDisplayDate(
                `${moment(queryStartDate, "YYYYMMDD").format("YYYY/MM/DD")} - ${moment(queryEndDate, "YYYYMMDD").format(
                    "YYYY/MM/DD"
                )}`
            );
        }
    }, [queryStartDate, queryEndDate, enableQueryDateRange, setDisplayDate]);

    useEffect(() => {
        if (!queryHour || !enableQueryHour || !setQueryHour) {
            setDisplayTime(emptyString.time);
        } else {
            setDisplayTime(moment(queryHour.$d).format("HH:00"));
        }
    }, [queryHour, setQueryHour, enableQueryHour]);

    useEffect(() => {
        if (queryGender === "" || !enableQueryGender || !setQueryGender) {
            setDisplayGender(emptyString.gender);
        } else {
            setDisplayGender(queryGender);
        }
    }, [queryGender, setQueryGender, enableQueryGender]);

    useEffect(() => {
        if (!Array.isArray(queryRooms) || queryRooms.length === 0 || !enableQueryRooms || !setQueryRooms) {
            setDisplayRooms(emptyString.rooms);
        } else {
            setDisplayRooms(`( ${queryRooms.length} )`);
        }
    }, [queryRooms, setQueryRooms, enableQueryRooms]);

    useEffect(() => {
        if (!Array.isArray(querySensors) || querySensors.length === 0 || !enableQuerySensors || !setQuerySensors) {
            setDisplaySensors(emptyString.sensors);
        } else {
            if (querySensors.length === 1 && querySensors[0] === "ALL") {
                setQuerySensors(SENSORS);
                setDisplaySensors(`( ${SENSORS.length} )`);
            } else {
                setDisplaySensors(`( ${querySensors.length} )`);
            }
        }
    }, [querySensors, setQuerySensors, enableQuerySensors]);

    useEffect(() => {
        setQueryRooms(selectedOptions.map((s) => s.value));
    }, [selectedOptions]);

    return (
        <div className="query-block">
            <div className="query-block-header">
                <div className="query-block-tags">
                    {enableQueryDateRange && (
                        <div className="query-block-tag" onClick={expandForm}>
                            <div className="query-block-tag-title">DATE</div>
                            <div className="query-block-tag-content">{displayDate}</div>
                        </div>
                    )}
                    {enableQueryHour && (
                        <div className="query-block-tag" onClick={expandForm}>
                            <div className="query-block-tag-title">TIME</div>
                            <div className="query-block-tag-content">{displayTime}</div>
                        </div>
                    )}
                    {enableQueryGender && (
                        <div className="query-block-tag" onClick={expandForm}>
                            <div className="query-block-tag-title">GENDER</div>
                            <div className="query-block-tag-content">{displayGender}</div>
                        </div>
                    )}
                    {enableQuerySensors && (
                        <div className="query-block-tag" onClick={expandForm}>
                            <div className="query-block-tag-title">SENSOR TYPE</div>
                            <div className="query-block-tag-content">{displaySensors}</div>
                        </div>
                    )}
                    {enableQueryRooms && (
                        <div className="query-block-tag" onClick={expandForm}>
                            <div className="query-block-tag-title">ROOMS</div>
                            <div className="query-block-tag-content">{displayRooms}</div>
                        </div>
                    )}
                    <div
                        className="query-block-tag query-block-submit"
                        onClick={() => {
                            if (isLoading) return;

                            if (queryStartDate === "" || queryEndDate === "") {
                                enqueueSnackbar("Please select date range", {
                                    variant: "warning",
                                });
                                return;
                            }
                            setDisplayForm(false);
                            handleSubmit();
                        }}
                    >
                        <div className="query-block-tag-title" style={{ cursor: isLoading ? "not-allowed" : "" }}>
                            Submit
                        </div>
                    </div>
                </div>
                <div className="query-block-btn" onClick={expandForm}>
                    {displayForm ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </div>
            </div>
            {displayForm && (
                <div className="query-block-form">
                    {enableQueryDateRange && (
                        <div className="query-date-range-form">
                            <div className="query-date-range-form-input" ref={dateRangeRef}></div>
                        </div>
                    )}
                    <div className="query-block-form-middle">
                        {enableQueryHour && (
                            <div className="query-hour-form">
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <TimePicker
                                        slotProps={{
                                            actionBar: {
                                                actions: ["clear", "accept"],
                                            },
                                        }}
                                        views={["hours"]}
                                        minutesStep={1}
                                        onChange={(v) => {
                                            setQueryHour(v);
                                        }}
                                        value={queryHour}
                                        sx={{ flex: 1 }}
                                        label={"Time"}
                                    />
                                </LocalizationProvider>
                            </div>
                        )}
                        {enableQueryGender && (
                            <div
                                className={`query-select-form ${
                                    enableQueryGender && enableQuerySensors
                                        ? "query-gender-form-small"
                                        : "query-gender-form-large"
                                }`}
                            >
                                <SelectFormTitle title={"Gender"} selectAllCb={() => setQueryGender("ALL")} />
                                {GENDERS.map((gender) => {
                                    return (
                                        <SelectFormLabel
                                            key={gender}
                                            label={gender}
                                            onClick={() => {
                                                setQueryGender(gender);
                                            }}
                                            selected={[gender, "ALL"].includes(queryGender)}
                                        />
                                    );
                                })}
                            </div>
                        )}
                        {enableQuerySensors && (
                            <div
                                className={`query-select-form ${
                                    enableQueryGender && enableQuerySensors
                                        ? "query-sensor-form-small"
                                        : "query-sensor-form-large"
                                }`}
                            >
                                <SelectFormTitle
                                    title={"Sensor Type"}
                                    selectAllCb={() => {
                                        if (querySensors.length === SENSORS.length) {
                                            setQuerySensors([]);
                                        } else {
                                            setQuerySensors(SENSORS);
                                        }
                                    }}
                                />
                                {SENSORS.map((sensor) => {
                                    return (
                                        <SelectFormLabel
                                            key={sensor}
                                            label={sensor}
                                            onClick={() => {
                                                if (querySensors.includes(sensor)) {
                                                    setQuerySensors(querySensors.filter((s) => s !== sensor));
                                                } else {
                                                    setQuerySensors([...querySensors, sensor]);
                                                }
                                            }}
                                            selected={querySensors.includes(sensor)}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className="query-room-form">
                        <div className="query-room-search">
                            <div className="query-room-input">
                                <Select
                                    ref={selectRef}
                                    classNamePrefix={"query-select"}
                                    className="query-room-input-element"
                                    styles={{
                                        container: (baseStyles) => ({
                                            ...baseStyles,
                                            borderRadius: "10px",
                                        }),
                                        placeholder: (baseStyles) => ({
                                            ...baseStyles,
                                            zIndex: 100,
                                        }),
                                        control: (baseStyles) => ({
                                            ...baseStyles,
                                            color: "black",
                                            backgroundColor: "#d9d9d9",
                                            borderRadius: "10px",
                                            border: "none",
                                            boxShadow: "none",
                                            ":hover": {
                                                border: "none",
                                            },
                                        }),
                                        option: (baseStyles) => ({
                                            ...baseStyles,
                                        }),
                                        groupHeading: (baseStyles) => ({
                                            ...baseStyles,
                                            color: "red",
                                        }),
                                        menuList: (baseStyles) => ({
                                            ...baseStyles,
                                            height: "228px",
                                            zIndex: "1000",
                                        }),
                                        menu: (baseStyles) => ({
                                            ...baseStyles,
                                            height: "228px",
                                            zIndex: "1000",
                                        }),
                                        indicatorSeparator: (baseStyles) => ({
                                            ...baseStyles,
                                            display: "none",
                                        }),
                                        dropdownIndicator: (baseStyles) => ({
                                            ...baseStyles,
                                            display: "none",
                                        }),
                                        indicatorsContainer: (baseStyles) => ({
                                            ...baseStyles,
                                        }),
                                        input: (baseStyles) => ({
                                            ...baseStyles,
                                            width: "100%",
                                        }),
                                    }}
                                    isMulti
                                    isClearable={true}
                                    inputValue={inputValue}
                                    onInputChange={(input) => setInputValue(input)}
                                    options={roomOptions}
                                    value={selectedOptions}
                                    onChange={(selected) => {
                                        const newSelected = JSON.parse(JSON.stringify(selected));
                                        newSelected.sort((a, b) => sortRooms(a, b));
                                        setSelectedOptions(newSelected);
                                    }}
                                    closeMenuOnSelect={false}
                                    placeholder="Select rooms"
                                    components={{
                                        GroupHeading,
                                        Option,
                                    }}
                                />
                                <div className="query-room-search-btn">
                                    <IoSearchSharp />
                                </div>
                            </div>
                        </div>
                        <div className="query-room-rank-shortcut"></div>
                        <div className="query-room-list"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QueryBlock;

const SelectFormTitle = ({ title, selectAllCb }) => {
    return (
        <div className="query-select-form-title">
            {title} <span onClick={selectAllCb}>Select All</span>
        </div>
    );
};

const SelectFormLabel = ({ label, onClick, selected }) => {
    return (
        <div
            className="query-select-form-label"
            onClick={onClick}
            style={{
                backgroundColor: selected ? "#e0ec86" : "",
            }}
        >
            {label}
        </div>
    );
};
